import React, {useEffect, useMemo, useState} from "react";
import {
    Alert,
    Box,
    Breadcrumbs,
    Button,
    Chip,
    CircularProgress,
    FormControlLabel,
    Link,
    Paper,
    Stack,
    Switch,
    TextField,
    Typography
} from "@mui/material";
import {
    Check as CheckIcon,
    DeleteOutline as DeleteIcon,
    LockOutlined as LockIcon,
    ReceiptLongOutlined as ReceiptIcon,
    SpeedOutlined as GaugeIcon
} from "@mui/icons-material";
import {Link as RouterLink, useNavigate, useParams} from "react-router-dom";
import {FeedbackSnackbar} from "../../shared/ui";
import {getBackendErrorMessage} from "../../shared/utils/httpError";
import {formatReais} from "../../shared/utils/formatters";
import {getSituacaoVersao} from "../PlanosComerciais/planoVersaoUtils";
import ConfirmacaoDialog from "../Sistemas/components/ConfirmacaoDialog";
import {SistemaLogo} from "../Sistemas/components/SistemaVisual";
import MetricasLayout from "./components/MetricasLayout";
import OpcoesEmCartao from "./components/OpcoesEmCartao";
import {useMetrica} from "./useMetrica";
import {
    AO_ATINGIR,
    APURACOES,
    AVISOS,
    FONTES,
    FORMAS,
    NATUREZAS,
    alternarAviso,
    apuracaoPermitida,
    escolher,
    exemploDaFatura,
    formDaMetrica,
    metricaAlterada,
    metricaVazia,
    nomeDaVersao,
    plural,
    quantidade,
    resumoDoLimite,
    rotulo,
    validarMetrica,
    versaoPublicada
} from "./metricasRegras";

const TRAVADO = 'Travado: a métrica está em versão publicada de plano.';

const opcoesDe = (tabela, {desligada = () => false, motivo = () => ''} = {}) => Object.entries(tabela).map(([value, item]) => ({
    value,
    titulo: item.label,
    texto: item.texto,
    disabled: desligada(value),
    motivo: motivo(value),
}));

// Cartão de um grupo de regras: medição, comportamento na licença ou faturamento.
const Regra = ({icone, titulo, texto, children}) => (
    <Paper variant="outlined" component="section" aria-label={titulo} sx={{p: {xs: 2, md: 3}}}>
        <Stack direction="row" spacing={1.5} alignItems="flex-start" sx={{mb: 2.5}}>
            <Box sx={{width: 36, height: 36, borderRadius: 1.5, display: 'grid', placeItems: 'center', bgcolor: 'action.hover', color: 'text.secondary', flexShrink: 0}}>{icone}</Box>
            <Box>
                <Typography variant="h6" component="h2" fontWeight={700}>{titulo}</Typography>
                <Typography variant="body2" color="text.secondary">{texto}</Typography>
            </Box>
        </Stack>
        <Stack spacing={2.5}>{children}</Stack>
    </Paper>
);

const Cartao = ({titulo, dica, children}) => (
    <Paper variant="outlined" component="section" aria-label={titulo}>
        <Box sx={{px: {xs: 2, md: 2.5}, pt: 2, pb: 1.5}}>
            <Typography variant="h6" component="h2" fontWeight={700}>{titulo}</Typography>
            {dica ? <Typography variant="caption" color="text.secondary">{dica}</Typography> : null}
        </Box>
        {children}
    </Paper>
);

const Rotulo = ({children}) => <Typography variant="caption" color="text.secondary" display="block" sx={{px: {xs: 2, md: 2.5}, pt: 1, pb: 0.5}}>{children}</Typography>;

const ItemDaLista = ({para, children}) => (
    <Box component={RouterLink} to={para} sx={{display: 'flex', gap: 1.5, alignItems: 'flex-start', px: {xs: 2, md: 2.5}, py: 1.25, borderTop: 1, borderColor: 'divider', color: 'inherit', textDecoration: 'none', '&:hover': {bgcolor: 'action.hover'}}}>
        {children}
    </Box>
);

function ExemploDaFatura({form, referencia}) {
    const exemplo = exemploDaFatura(form, referencia);
    const {linha} = exemplo;
    return (
        <Cartao titulo="Como aparece na fatura" dica={exemplo.origem}>
            <Stack spacing={1.5} sx={{px: {xs: 2, md: 2.5}, pb: 2.5}}>
                <Typography variant="body2">{exemplo.texto}</Typography>
                <Box sx={{p: 1.5, borderRadius: 1.5, border: 1, borderColor: linha.proxima ? 'info.light' : 'divider', bgcolor: linha.proxima ? 'rgba(2, 136, 209, 0.06)' : 'transparent'}}>
                    <Stack direction="row" spacing={1} justifyContent="space-between" alignItems="baseline">
                        <Typography variant="body2" fontWeight={600} component="div">
                            {exemplo.nome} {linha.proxima ? <Chip size="small" color="info" variant="outlined" label="fatura seguinte" sx={{ml: 0.5}} /> : null}
                        </Typography>
                        <Typography variant="body2" fontWeight={700} sx={{whiteSpace: 'nowrap'}}>{linha.valor ? formatReais(linha.valor) : '—'}</Typography>
                    </Stack>
                    <Typography variant="caption" color="text.secondary" display="block" sx={{mt: 0.5}}>
                        {linha.texto}{linha.aviso ? ` ${linha.aviso}` : ''}
                    </Typography>
                </Box>
                <Alert severity="info" variant="outlined" icon={<ReceiptIcon fontSize="small" />}>
                    <strong>Excedente cobrado na fatura seguinte.</strong> Regra fixa para todas as métricas.
                </Alert>
            </Stack>
        </Cartao>
    );
}

function UsadaPor({metrica, usos}) {
    const sistemas = metrica.sistemas || [];
    return (
        <Cartao titulo="Usada por">
            <Rotulo>Sistemas que apontam</Rotulo>
            {usos.sistemas.length ? usos.sistemas.map(({sistema, apontamento}) => (
                <ItemDaLista key={sistema.id} para={`/sistemas/${sistema.id}/metricas`}>
                    <SistemaLogo sistema={sistema} tamanho={32} />
                    <Box sx={{minWidth: 0}}>
                        <Typography variant="body2" fontWeight={700}>{sistema.nome}</Typography>
                        {apontamento ? <Typography variant="caption" color="text.secondary">{rotulo(FONTES, apontamento.fonte)} · {apontamento.como_mede}</Typography> : null}
                    </Box>
                </ItemDaLista>
            )) : <Typography variant="body2" color="text.secondary" sx={{px: {xs: 2, md: 2.5}, pb: 1}}>Nenhum sistema aponta esta métrica.</Typography>}
            <Rotulo>Versões de plano</Rotulo>
            {usos.versoes.length ? usos.versoes.map(({versao, limite}) => {
                const situacao = getSituacaoVersao(versao.situacao);
                const sistema = sistemas.find((item) => item.id === versao.id_sistema);
                return (
                    <ItemDaLista key={`${versao.id_plano}-${versao.numero}`} para={`/planos-comerciais/${versao.id_plano}/versoes/${versao.numero}`}>
                        <Box sx={{flex: 1, minWidth: 0}}>
                            <Typography variant="body2" fontWeight={700}>{sistema ? `${sistema.nome} ` : ''}{versao.nome_plano} · versão {versao.numero}</Typography>
                            <Typography variant="caption" color="text.secondary">{limite ? resumoDoLimite(metrica, limite) : 'Carregando o limite…'}</Typography>
                        </Box>
                        <Chip size="small" variant="outlined" color={situacao.color} label={situacao.label} />
                    </ItemDaLista>
                );
            }) : <Typography variant="body2" color="text.secondary" sx={{px: {xs: 2, md: 2.5}, pb: 2}}>Nenhum plano usa esta métrica.</Typography>}
            <Box sx={{pb: 1}} />
        </Cartao>
    );
}

function EditorDaMetrica({id, onFeedback}) {
    const navigate = useNavigate();
    const dados = useMetrica(id);
    const {nova, metrica, usos, carregando, erro} = dados;
    const [form, setForm] = useState(() => metricaVazia());
    const [salvando, setSalvando] = useState(false);
    const [excluindo, setExcluindo] = useState(false);

    useEffect(() => {
        if (metrica) setForm(formDaMetrica(metrica));
    }, [metrica]);

    const sujo = nova || (metrica ? metricaAlterada(form, metrica) : false);
    const travada = Boolean(metrica?.travada);
    const publicadas = (metrica?.versoes || []).filter(versaoPublicada);
    const planosPublicados = new Set(publicadas.map((versao) => versao.id_plano)).size;
    const podeExcluir = !nova && metrica && !(metrica.sistemas || []).length && !(metrica.versoes || []).length;

    // O exemplo usa os valores da primeira versão publicada com limite desta métrica (ou da primeira com limite).
    const referencia = useMemo(() => {
        const comLimite = usos.versoes.filter((item) => item.limite);
        const escolhida = comLimite.find((item) => versaoPublicada(item.versao)) || comLimite[0];
        return escolhida ? {nome: nomeDaVersao(escolhida.versao, metrica?.sistemas), limite: escolhida.limite} : null;
    }, [metrica, usos.versoes]);

    const campo = (nome) => (event) => setForm((atual) => ({...atual, [nome]: event.target.value}));
    const opcao = (nome) => (valor) => setForm((atual) => escolher(atual, nome, valor));

    const salvar = async () => {
        const problema = validarMetrica(form, nova);
        if (problema) {
            onFeedback({error: problema, success: ''});
            return;
        }
        setSalvando(true);
        try {
            if (nova) {
                const novoId = await dados.criar(form);
                onFeedback({error: '', success: `Métrica "${form.nome.trim()}" criada. Aponte-a nos sistemas que a medem, na seção Métricas de cada sistema.`});
                navigate(novoId ? `/metricas/${novoId}` : '/metricas', {replace: true});
            } else {
                await dados.salvar(form);
                onFeedback({error: '', success: `Métrica salva.${publicadas.length ? ' Vale para as próximas faturas.' : ''}`});
            }
        } catch (error) {
            onFeedback({error: getBackendErrorMessage(error, 'Não foi possível salvar a métrica.'), success: ''});
        } finally {
            setSalvando(false);
        }
    };

    const descartar = () => {
        if (nova) navigate('/metricas');
        else setForm(formDaMetrica(metrica));
    };

    if (carregando && !metrica) {
        return (
            <Stack direction="row" spacing={1} alignItems="center" justifyContent="center" sx={{py: 6}}>
                <CircularProgress size={20} />
                <Typography color="text.secondary">Carregando a métrica…</Typography>
            </Stack>
        );
    }
    if (erro) return <Alert severity="error">{erro}</Alert>;

    const consumo = form.natureza === 'CONSUMO';
    const blocoNaUnidade = {...form, unidade_singular: form.unidade_singular || 'unidade', unidade_plural: form.unidade_plural || 'unidades'};

    return (
        <Stack spacing={3}>
            <Box>
                <Breadcrumbs>
                    <Link component={RouterLink} to="/metricas" underline="hover" color="inherit">Métricas</Link>
                    <Typography color="text.primary">{nova ? 'Nova métrica' : metrica.nome}</Typography>
                </Breadcrumbs>
                <Typography variant="h4" component="h1" fontWeight={700} sx={{mt: 1, overflowWrap: 'anywhere'}}>{nova ? 'Nova métrica' : metrica.nome}</Typography>
                <Typography variant="body1" color="text.secondary">
                    {nova ? 'Cadastre a métrica. Depois, aponte-a nos sistemas que a medem.' : (metrica.descricao || 'Sem descrição.')}
                </Typography>
            </Box>

            {travada ? (
                <Alert severity="warning" icon={<LockIcon />}>
                    <strong>Natureza, apuração e forma de cobrança estão travadas.</strong> A métrica está em {publicadas.map((versao) => nomeDaVersao(versao, metrica.sistemas)).join(', ')}.
                    {' '}Mudar como mede ou cobra mudaria o preço de quem já assina. Para isso, cadastre uma métrica nova. O resto vale para as próximas faturas.
                </Alert>
            ) : null}

            <Box sx={{display: 'grid', gap: 3, alignItems: 'start', gridTemplateColumns: {xs: 'minmax(0, 1fr)', lg: 'minmax(0, 3fr) minmax(0, 2fr)'}}}>
                <Stack spacing={3} sx={{minWidth: 0}}>
                    <Paper variant="outlined" component="section" aria-label="Identificação" sx={{p: {xs: 2, md: 3}}}>
                        <Typography variant="h6" component="h2" fontWeight={700} sx={{mb: 2}}>Identificação</Typography>
                        <Box sx={{display: 'grid', gap: 2.5, gridTemplateColumns: {xs: '1fr', md: '1fr 1fr'}}}>
                            {nova ? (
                                <TextField
                                    label="Chave"
                                    value={form.chave}
                                    onChange={(event) => setForm((atual) => ({...atual, chave: event.target.value.trim().toLowerCase()}))}
                                    placeholder="ex.: armazenamento"
                                    helperText="Não muda depois de salva. Sistemas e planos apontam para ela."
                                    inputProps={{autoComplete: 'off', spellCheck: false, maxLength: 60, style: {fontFamily: 'monospace'}}}
                                />
                            ) : (
                                <Box>
                                    <Typography variant="caption" color="text.secondary" display="block">Chave</Typography>
                                    <Stack direction="row" spacing={1} alignItems="center" sx={{mt: 0.5, px: 1.5, py: 1, border: 1, borderColor: 'divider', borderRadius: 1, bgcolor: 'action.hover', width: 'fit-content'}}>
                                        <LockIcon fontSize="small" color="action" />
                                        <Typography variant="body2" sx={{fontFamily: 'monospace'}}>{metrica.chave}</Typography>
                                    </Stack>
                                    <Typography variant="caption" color="text.secondary">Estável: sistemas e planos apontam para ela.</Typography>
                                </Box>
                            )}
                            <TextField label="Nome" value={form.nome} onChange={campo('nome')} inputProps={{maxLength: 120}} />
                            <Box sx={{gridColumn: {md: '1 / -1'}}}>
                                <TextField label="Descrição" value={form.descricao} onChange={campo('descricao')} fullWidth />
                            </Box>
                            <TextField label="Unidade no singular" value={form.unidade_singular} onChange={campo('unidade_singular')} placeholder="usuário" inputProps={{maxLength: 60}} />
                            <TextField label="Unidade no plural" value={form.unidade_plural} onChange={campo('unidade_plural')} placeholder="usuários" inputProps={{maxLength: 60}} />
                        </Box>
                    </Paper>

                    <Regra icone={<GaugeIcon fontSize="small" />} titulo="Medição" texto="O que a métrica conta e como chega a um número por ciclo.">
                        <OpcoesEmCartao
                            rotulo="Natureza"
                            valor={form.natureza}
                            opcoes={opcoesDe(NATUREZAS, {desligada: () => travada, motivo: () => TRAVADO})}
                            onEscolher={opcao('natureza')}
                        />
                        <Box>
                            <OpcoesEmCartao
                                rotulo="Apuração no ciclo"
                                valor={form.apuracao}
                                opcoes={opcoesDe(APURACOES, {
                                    desligada: (valor) => travada || !apuracaoPermitida(form.natureza, valor),
                                    motivo: () => (travada ? TRAVADO : ''),
                                })}
                                onEscolher={opcao('apuracao')}
                            />
                            <Typography variant="caption" color="text.secondary" display="block" sx={{mt: 1}}>
                                {consumo ? 'Consumo só aceita soma.' : 'Estado usa pico, média diária ou fechamento.'}
                            </Typography>
                        </Box>
                    </Regra>

                    <Regra icone={<LockIcon fontSize="small" />} titulo="Comportamento na licença" texto="O padrão para os planos. Cada plano pode trocar o que acontece ao atingir o limite.">
                        <OpcoesEmCartao rotulo="Ao atingir o limite" valor={form.ao_atingir} opcoes={opcoesDe(AO_ATINGIR)} onEscolher={opcao('ao_atingir')} />
                        <Box>
                            <Typography variant="caption" color="text.secondary" display="block" sx={{mb: 1}}>Avisos de consumo</Typography>
                            <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap" role="group" aria-label="Avisos de consumo">
                                {AVISOS.map((aviso) => {
                                    const marcado = form.avisos.includes(aviso);
                                    return (
                                        <Chip
                                            key={aviso}
                                            label={`${aviso}%`}
                                            icon={marcado ? <CheckIcon fontSize="small" /> : undefined}
                                            color={marcado ? 'primary' : 'default'}
                                            variant={marcado ? 'filled' : 'outlined'}
                                            aria-pressed={marcado}
                                            onClick={() => setForm((atual) => alternarAviso(atual, aviso))}
                                        />
                                    );
                                })}
                            </Stack>
                        </Box>
                        <Box>
                            <FormControlLabel
                                control={<Switch checked={form.permite_teto} onChange={(event) => setForm((atual) => ({...atual, permite_teto: event.target.checked}))} />}
                                label="O plano pode definir um teto de segurança"
                            />
                            <Typography variant="caption" color="text.secondary" display="block">
                                O teto é um múltiplo do limite. Só vale com "liberar" e quando o sistema consegue bloquear: evita uma fatura de excedente que ninguém esperava.
                            </Typography>
                        </Box>
                    </Regra>

                    <Regra icone={<ReceiptIcon fontSize="small" />} titulo="Faturamento" texto="Como o excedente vira valor na fatura.">
                        <OpcoesEmCartao
                            rotulo="Forma de cobrança"
                            valor={form.forma_cobranca}
                            opcoes={opcoesDe(FORMAS, {desligada: () => travada, motivo: () => TRAVADO})}
                            onEscolher={opcao('forma_cobranca')}
                        />
                        {form.forma_cobranca === 'BLOCO' ? (
                            <Box sx={{display: 'grid', gap: 2.5, gridTemplateColumns: {xs: '1fr', md: '1fr 1fr'}}}>
                                <TextField
                                    label="Tamanho do bloco"
                                    value={form.bloco_tamanho}
                                    onChange={(event) => setForm((atual) => ({...atual, bloco_tamanho: event.target.value.replace(/\D/g, '')}))}
                                    disabled={travada}
                                    helperText={form.bloco_tamanho ? `= ${quantidade(blocoNaUnidade, form.bloco_tamanho)}` : 'Quantas unidades formam um bloco.'}
                                    inputProps={{inputMode: 'numeric'}}
                                />
                                <TextField label="Nome do bloco" value={form.bloco_nome} onChange={campo('bloco_nome')} placeholder="milhão de tokens" inputProps={{maxLength: 60}} />
                            </Box>
                        ) : null}
                        <Alert severity="info" variant="outlined">Regra fixa: <strong>excedente cobrado na fatura seguinte.</strong></Alert>
                    </Regra>
                </Stack>

                <Stack spacing={3} sx={{minWidth: 0, position: {lg: 'sticky'}, top: {lg: 88}}}>
                    <ExemploDaFatura form={form} referencia={referencia} />
                    {!nova ? <UsadaPor metrica={metrica} usos={usos} /> : null}
                </Stack>
            </Box>

            <Paper
                variant="outlined"
                sx={{position: 'sticky', bottom: 16, zIndex: 2, px: 2, py: 1.5, display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap', boxShadow: 3}}
            >
                <Box sx={{flex: '1 1 240px', minWidth: 0}}>
                    {sujo ? (
                        <Typography variant="body2">
                            <strong>{nova ? 'Métrica nova, ainda não salva.' : 'Alterações não salvas.'}</strong>
                            {!nova && planosPublicados ? ` Vale para as próximas faturas de ${plural(planosPublicados, 'plano', 'planos')}.` : ''}
                        </Typography>
                    ) : <Typography variant="body2" color="text.secondary">Nenhuma alteração.</Typography>}
                </Box>
                {podeExcluir ? <Button color="error" variant="outlined" startIcon={<DeleteIcon />} onClick={() => setExcluindo(true)} disabled={salvando}>Excluir</Button> : null}
                <Button variant="outlined" onClick={descartar} disabled={!sujo || salvando}>Descartar</Button>
                <Button variant="contained" startIcon={<CheckIcon />} onClick={salvar} disabled={!sujo || salvando}>
                    {salvando ? 'Salvando…' : nova ? 'Criar métrica' : 'Salvar'}
                </Button>
            </Paper>

            <ConfirmacaoDialog
                aberto={excluindo}
                titulo={`Excluir a métrica ${metrica?.nome || ''}?`}
                botao="Excluir"
                perigo
                onConfirmar={async () => {
                    await dados.excluir();
                    setExcluindo(false);
                    onFeedback({error: '', success: 'Métrica excluída.'});
                    navigate('/metricas');
                }}
                onFechar={() => setExcluindo(false)}
            >
                <Typography variant="body2">Nenhum sistema aponta esta métrica e nenhum plano a usa. Ela sai do cadastro.</Typography>
            </ConfirmacaoDialog>
        </Stack>
    );
}

/*
 * Cadastro de uma métrica (BBF-167 a, protótipo v2 "Métricas"): identificação e os três grupos de regras, com o
 * exemplo da fatura ao lado, que muda conforme a escolha. A edição fica num rascunho na tela e só vale ao salvar.
 */
export default function MetricaPage({openParent = true}) {
    const {id} = useParams();
    const [feedback, setFeedback] = useState({error: '', success: ''});
    return (
        <MetricasLayout openParent={openParent}>
            <EditorDaMetrica key={id} id={id} onFeedback={setFeedback} />
            <FeedbackSnackbar feedback={feedback} onClose={() => setFeedback({error: '', success: ''})} />
        </MetricasLayout>
    );
}
